import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { atom, SetStateAction, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { mapValues } from "lodash";
import { useMemo } from "react";
import { useSelector } from "store/useSelector";
import { useMachemetryTicks } from "components/DataGetter/DataDumpRowsGetterForSingleTimeline";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { ONE_MINUTE_OF_MS } from "utils/timeUtils";
import {
  CustomerJsonType,
  DataDumpRow,
  ManyProductionSegmentsArrays,
  ProductionSegmentType,
  TagDetails,
  TimeRangeFinite,
} from "types/types";
import {
  useCustomerJson,
  useIsAdmin,
  useGetPageFromPath,
  useIsSingleProcessPage,
} from "utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { speedGainLossTargetSpeedAtom } from "components/Dashboard/Timeline/TimelineCardControls/TimelinesConfig";

// jotai is a global state manager like redux, but much simpler
// jotai + typescript https://docs.pmnd.rs/jotai/guides/typescript

const highlightedTagsAtom = atom<HighlightedTagType[]>([]);

type HighlightedTagType = TagDetails & { highlightColor: string };

/** [list, setList] of tags that are currently highlighted on the timeline e.g. https://www.figma.com/file/gp9JWxMgRdAt0DgUxP70PV/Handoff-UI-Doc?node-id=0%3A1 */
export function useHighlightedTags(): [
  HighlightedTagType[],
  (update: React.SetStateAction<HighlightedTagType[]>) => void,
] {
  const { isTrendsPage, isTimelinePage } = useGetPageFromPath();
  const isStorybook = useIsStorybook();
  const isSingleProcessPage = useIsSingleProcessPage();

  const [
    {
      general: { trendsPageShowTagHighlight, timelinePageShowTagHighlight },
    },
  ] = useFlightConfig();
  const [highlightedTags, setHighlightedTags] = useAtom(highlightedTagsAtom);
  // highlighted tags are only allowed on:
  // - trends page if flag enabled
  // - single-process page
  const isHighlightedTagsAllowed =
    (isTrendsPage && trendsPageShowTagHighlight) ||
    (isTimelinePage && timelinePageShowTagHighlight) ||
    isSingleProcessPage ||
    isStorybook;
  return [isHighlightedTagsAllowed ? highlightedTags : [], setHighlightedTags];
}

/** colors used in HighlightTagsSelector to indicate highlighted tags */
export const HIGHLIGHT_COLORS = [
  "#40F09C",
  "#EF18DA",
  "#227AFF",
  "#9DC5D1",
  "#60E90D",
  "#986A26",
  "#B60A14",
  "#7A7A7A",
  "#BBB753",
];

const isTimelineExpandedAtom = atomWithStorage<boolean>(
  "atom:isTimelineExpanded",
  false
);
export function useIsTimelineExpanded(): [
  boolean,
  (update: SetStateAction<boolean>) => void,
] {
  const [isTimelineExpanded, setIsTimelineExpanded] = useAtom(
    isTimelineExpandedAtom
  );
  const { isHubletPage } = useGetPageFromPath();
  return [isHubletPage ? isTimelineExpanded : false, setIsTimelineExpanded];
}

export type SkuTimelineSegment = {
  range: {
    ge: number;
    lt?: number;
  };
  skuCode: string;
  skuName: string;
  skuScale: number;
  targetRate: number;
  rateDenominator: number;
  teamSize: number;
};
export type CountTimelineSegment = {
  range: {
    ge: number;
    lt: number;
  };
  netOutput: number;
};
export type RejectCountTimelineSegment = {
  range: {
    ge: number;
    lt: number;
  };
  rejectOutput: number;
};

const dataDumpRowsAtom = atom<{
  [machemetryId: string]: DataDumpRow[];
}>({});
export function useDataDumpRows() {
  return useAtom(dataDumpRowsAtom);
}

export type OperatorTimelineSegment = {
  range: {
    ge: number;
    lt: number;
  };
  operatorId: string;
  operatorName: string;
  avatar_letters?: string;
  avatar_letters_bg?: string;
  avatar_letters_fg?: string;
};
export type TeamSizeTimelineSegment = {
  range: {
    ge: number;
    lt?: number;
  };
  teamSize: number;
};

const alternateTimelinesAtom = atom<ManyProductionSegmentsArrays>({});

/** timeline segments from askr, but filtered by datasourceGroupIds, to display below each timeline (internal-only feature) */
export function useAlternateTimelines(): [
  ManyProductionSegmentsArrays,
  (update: React.SetStateAction<ManyProductionSegmentsArrays>) => void,
] {
  const [alternateTimelines, setAlternateTimelines] = useAtom(
    alternateTimelinesAtom
  );

  const isAdminUser = useIsAdmin();
  const { flightEnableAlternateTimeline } = useFlagsWithDefaults();
  const isAlternateTimelinesAllowed =
    isAdminUser || flightEnableAlternateTimeline;

  return useMemo(
    () => [
      isAlternateTimelinesAllowed ? alternateTimelines : {},
      setAlternateTimelines,
    ],
    [alternateTimelines, isAlternateTimelinesAllowed, setAlternateTimelines]
  );
}

type DataSourceGroup = { name: string; id: string };
const dataSourceGroupsAtom = atom<DataSourceGroup[]>([]);
const selectedDataSourceGroupsAtom = atom<DataSourceGroup[]>([]);

/** timeline segments from askr, but filtered by datasourceGroupIds, to display below each timeline (internal-only feature) */
export function useDataSourceGroups(): {
  dataSourceGroups: DataSourceGroup[];
  setDataSourceGroups: (
    update: React.SetStateAction<DataSourceGroup[]>
  ) => void;
  selectedDataSourceGroups: DataSourceGroup[];
  setSelectedDataSourceGroups: (
    update: React.SetStateAction<DataSourceGroup[]>
  ) => void;
} {
  const [dataSourceGroups, setDataSourceGroups] = useAtom(dataSourceGroupsAtom);
  const [selectedDataSourceGroups, setSelectedDataSourceGroups] = useAtom(
    selectedDataSourceGroupsAtom
  );

  return useMemo(
    () => ({
      dataSourceGroups,
      setDataSourceGroups,
      selectedDataSourceGroups,
      setSelectedDataSourceGroups,
    }),
    [
      dataSourceGroups,
      setDataSourceGroups,
      selectedDataSourceGroups,
      setSelectedDataSourceGroups,
    ]
  );
}

export function useCountTimelineSegments() {
  const [dataDumpRows] = useDataDumpRows();
  const countTimelineSegments = useMemo(() => {
    const segments = mapValues(dataDumpRows, getCountTimelineSegments);
    return segments;
  }, [dataDumpRows]);
  return countTimelineSegments;
}

function getCountTimelineSegments(dataDumpRows: DataDumpRow[]) {
  const countsSegments: CountTimelineSegment[] = dataDumpRows.map(row => ({
    range: {
      ge: Number(row.ge),
      lt: Number(row.lt),
    },
    netOutput: row.netOutput,
  }));
  return countsSegments;
}

export function useRejectCountTimelineSegments() {
  const [dataDumpRows] = useDataDumpRows();
  const rejectCountTimelineSegments = useMemo(() => {
    const segments = mapValues(dataDumpRows, getRejectCountTimelineSegments);
    return segments;
  }, [dataDumpRows]);
  return rejectCountTimelineSegments;
}
function getRejectCountTimelineSegments(dataDumpRows: DataDumpRow[]) {
  const rejectCountsSegments: RejectCountTimelineSegment[] = dataDumpRows.map(
    row => ({
      range: {
        ge: Number(row.ge),
        lt: Number(row.lt),
      },
      rejectOutput: row.rejectOutput,
    })
  );
  return rejectCountsSegments;
}

export function useTeamSizeTimelineSegments() {
  const [dataDumpRows] = useDataDumpRows();
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const teamSizeTimelineSegments = useMemo(() => {
    const segments = mapValues<
      { [machemetryId: string]: DataDumpRow[] },
      TeamSizeTimelineSegment[]
    >(dataDumpRows, row => getTeamSizeSegments(row, visibleRange));
    return segments;
  }, [dataDumpRows, visibleRange]);
  return teamSizeTimelineSegments;
}

function getTeamSizeSegments(
  dataDumpRows: DataDumpRow[],
  visibleRange: TimeRangeFinite | null
): TeamSizeTimelineSegment[] {
  return dataDumpRows.reduce((acc, cur, idx) => {
    if (idx === 0) {
      return [
        {
          teamSize: cur.teamSize,
          range: { ge: Number(cur.ge), lt: undefined },
        },
      ];
    }

    const lastSegment = acc[acc.length - 1];

    const isNewSegment = lastSegment.teamSize !== cur.teamSize;
    if (isNewSegment) {
      acc = [
        ...acc.slice(0, -1), // remove the last segment, then put it back modified
        // 1. set the lt to close the previous segment
        {
          ...lastSegment,
          range: { ...lastSegment.range, lt: Number(cur.ge) },
        },
        // 2. add the new segment
        {
          teamSize: cur.teamSize,
          range: { ge: Number(cur.ge), lt: undefined },
        },
      ];
    }

    // 3. set the lt of the final segment
    const isFinalSegment = idx === dataDumpRows.length - 1;
    if (isFinalSegment) {
      const finalSegment = acc[acc.length - 1];
      acc = [
        ...acc.slice(0, -1), // remove the last segment, then put it back modified
        {
          ...finalSegment,
          range: {
            ...finalSegment.range,
            lt: Number(cur.lt) || visibleRange?.lt,
          },
        },
      ];
    }

    return acc;
  }, [] as TeamSizeTimelineSegment[]) as unknown as TeamSizeTimelineSegment[];
}

export function useSkuTimelineSegments(): {
  [machemetryId: string]: SkuTimelineSegment[];
} {
  const [dataDumpRows] = useDataDumpRows();
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const skuTimelineSegments = useMemo(() => {
    const segments = mapValues<
      { [machemetryId: string]: DataDumpRow[] },
      SkuTimelineSegment[]
    >(dataDumpRows, rows => getSkuSegments(rows, visibleRange));
    return segments;
  }, [dataDumpRows, visibleRange]);
  return skuTimelineSegments;
}

function getSkuSegments(
  dataDumpRows: DataDumpRow[],
  visibleRange: TimeRangeFinite | null
): SkuTimelineSegment[] {
  return dataDumpRows?.reduce((acc, cur, idx) => {
    if (idx === 0) {
      return [
        {
          skuCode: cur.skuCode,
          skuName: cur.skuName,
          skuScale: cur.skuScale,
          teamSize: cur.teamSize,
          targetRate: cur.targetRate,
          rateDenominator: cur.rateDenominator,
          range: { ge: Number(cur.ge), lt: undefined },
        },
      ];
    }

    const lastSegment = acc[acc.length - 1];

    const isNewSegment = lastSegment.skuCode !== cur.skuCode;
    if (isNewSegment) {
      acc = [
        ...acc.slice(0, -1), // remove the last segment, then put it back modified
        // 1. set the lt to close the previous segment
        {
          ...lastSegment,
          range: { ...lastSegment.range, lt: Number(cur.ge) },
        },
        // 2. add the new segment
        {
          skuCode: cur.skuCode,
          skuName: cur.skuName,
          skuScale: cur.skuScale,
          targetRate: cur.targetRate,
          rateDenominator: cur.rateDenominator,
          teamSize: cur.teamSize,
          range: { ge: Number(cur.ge), lt: undefined },
        },
      ];
    }

    // 3. set the lt of the final segment
    const isFinalSegment = idx === dataDumpRows.length - 1;
    const finalSegment = acc[acc.length - 1];
    if (isFinalSegment) {
      acc = [
        ...acc.slice(0, -1), // remove the last segment, then put it back modified
        {
          ...finalSegment,
          range: {
            ...finalSegment.range,
            lt: Number(cur.lt) || visibleRange?.lt,
          },
        },
      ];
    }

    return acc;
  }, [] as SkuTimelineSegment[]) as unknown as SkuTimelineSegment[];
}

export function useOperatorTimelineSegments() {
  const [dataDumpRows] = useDataDumpRows();
  const customerJson = useCustomerJson();
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const operatorTimelineSegments = useMemo(() => {
    const segments = mapValues(dataDumpRows, row =>
      getOperatorSegments(row, customerJson, visibleRange)
    );
    return segments;
  }, [dataDumpRows, customerJson, visibleRange]);
  return operatorTimelineSegments;
}

function getOperatorSegments(
  dataDumpRows: any,
  customerJson: CustomerJsonType | null,
  visibleRange: TimeRangeFinite | null
): OperatorTimelineSegment[] {
  return dataDumpRows?.reduce((acc, cur, idx) => {
    const operator = customerJson?.operatorGroups[cur.operatorId];
    customerJson?.operatorGroups[cur.operatorId];
    customerJson?.operatorGroups[cur.operatorId];
    customerJson?.operatorGroups[cur.operatorId];
    if (idx === 0) {
      return [
        {
          operatorId: cur.operatorId,
          operatorName: operator?.name || "",
          avatar_letters: operator?.avatar_letters || "",
          avatar_letters_bg: operator?.avatar_letters_bg || "",
          avatar_letters_fg: operator?.avatar_letters_fg || "",
          range: { ge: Number(cur.ge), lt: undefined },
        },
      ];
    }

    const lastSegment = acc[acc.length - 1];

    const isNewSegment = lastSegment.operatorId !== cur.operatorId;
    if (isNewSegment) {
      acc = [
        ...acc.slice(0, -1),
        // 1. set the lt to close the previous segment
        {
          ...lastSegment,
          range: { ...lastSegment.range, lt: Number(cur.ge) },
        },
        // 2. add the new segment
        {
          operatorId: cur.operatorId,
          operatorName: operator?.name || "",
          avatar_letters: operator?.avatar_letters || "",
          avatar_letters_bg: operator?.avatar_letters_bg || "",
          avatar_letters_fg: operator?.avatar_letters_fg || "",
          range: { ge: Number(cur.ge), lt: undefined },
        },
      ];
    }

    // 3. set the lt of the final segment
    const finalSegment = acc[acc.length - 1];
    const isFinalSegment = idx === dataDumpRows.length - 1;
    if (isFinalSegment) {
      acc = [
        ...acc.slice(0, -1),
        {
          ...finalSegment,
          range: {
            ...finalSegment.range,
            lt: Number(cur.lt) || visibleRange?.lt,
          },
        },
      ];
    }

    return acc;
  }, []);
}

export enum SpeedCategory {
  ABOVE = "above",
  AT = "at",
  BELOW = "below",
}

export type SpeedGainLossTimelineSegment = {
  range: TimeRangeFinite;
  category: SpeedCategory;
  speedPercentage: number; // Percentage above/below target speed
  targetOutput: number;
  actualOutput: number;
  targetRate: number;
};

export function useSpeedGainLossTimelineSegments(): Record<
  string,
  SpeedGainLossTimelineSegment[]
> {
  // Get process ID from the legacy store
  const processId = useProcessIdOnSingleProcessPage();
  // Get all relevant ticks for determining speed differences
  const [machemetryTicks] = useMachemetryTicks();

  // Get data dump rows for target rate information
  const [dataDumpRows] = useDataDumpRows();
  // Get target speed from atom
  const [speedGainLossTargetSpeed] = useAtom(speedGainLossTargetSpeedAtom);
  const productionSegments = useSelector(state =>
    processId ? state.legacy.segments[processId] || [] : []
  );
  // Generate and memoize speed gain/loss segments
  const speedGainLossSegments = useMemo(() => {
    if (!processId || !machemetryTicks) {
      return {};
    }

    // Get the segments for this process
    const processSegments = getSpeedGainLossSegments(
      machemetryTicks,
      dataDumpRows[processId] || [],
      productionSegments,
      speedGainLossTargetSpeed
    );

    return { [processId]: processSegments };
  }, [
    machemetryTicks,
    dataDumpRows,
    processId,
    speedGainLossTargetSpeed,
    productionSegments,
  ]);
  return speedGainLossSegments;
}

/**
 * Generates speed gain/loss segments based on ticks data
 *
 * @param machemetryTicks - Object containing gross, reject, and net ticks data
 * @param dataDumpRows - Data dump rows containing target rate information
 * @returns Array of speed gain/loss segments
 */
function getSpeedGainLossSegments(
  machemetryTicks: {
    grossCount_0: { ts: number; v: any }[];
    rejectCount_0: { ts: number; v: any }[];
    netCount_0: { ts: number; v: any }[];
  },
  dataDumpRows: any, // Accept any data structure that might contain target rate info,
  productionSegments: ProductionSegmentType[],
  speedGainLossTargetSpeed: number
): SpeedGainLossTimelineSegment[] {
  // Use whichever has more data points - prefer net count, then gross count
  const ticksData: { ts: number; v: any }[] =
    machemetryTicks.netCount_0?.length > 0
      ? machemetryTicks.netCount_0
      : machemetryTicks.grossCount_0?.length > 0
        ? machemetryTicks.grossCount_0
        : [];

  if (ticksData.length < 2) {
    console.log("Not enough ticks to create segments");
    return [];
  }

  // Sort ticks by timestamp to ensure proper ordering
  const sortedTicks = [...ticksData].sort((a, b) => a.ts - b.ts);

  // Find a default target rate from data dump rows if available
  let defaultTargetRate = 60; // Default to 1 per minute if not found
  if (Array.isArray(dataDumpRows) && dataDumpRows.length > 0) {
    // Find any row with a target rate
    const rowWithTargetRate = dataDumpRows.find(
      (row: any) =>
        row && typeof row.targetRate === "number" && row.targetRate > 0
    );

    if (rowWithTargetRate) {
      defaultTargetRate = Number(rowWithTargetRate.targetRate);
    }
  }

  // Create segments between each pair of adjacent ticks
  const segments: SpeedGainLossTimelineSegment[] = [];

  for (let i = 1; i < sortedTicks.length; i++) {
    const prevTick = sortedTicks[i - 1];
    const currentTick = sortedTicks[i];

    // Time between ticks in milliseconds
    const actualTimeMs = (currentTick.ts - prevTick.ts) * 1000;

    // Skip unreasonable time intervals (if ticks are too close or too far apart)
    // Between 100ms and 7200000ms (2 hours)
    if (actualTimeMs < 100 || actualTimeMs > 7200000) {
      // leaving here for debugging purposes, but too messy in prod
      // console.log("Skipping segment due to time interval:", {
      //   from: new Date(prevTick.ts * 1000).toLocaleTimeString(),
      //   to: new Date(currentTick.ts * 1000).toLocaleTimeString(),
      //   actualTimeMs,
      //   tooShort: actualTimeMs < 100,
      //   tooLong: actualTimeMs > 7200000,
      // });
      continue;
    }

    // Check if this tick interval overlaps with a non-producing segment
    const speedStart = prevTick.ts * 1000;
    const speedEnd = currentTick.ts * 1000;

    // Check if there's any matching production segment that is not producing
    const nonProducingSegment = productionSegments.find(segment => {
      // Check if the segment has the necessary properties
      if (!segment || !segment.range) {
        return false;
      }

      // Look for any non-producing segments
      const segmentCategory = segment.pnl?.category;

      // Only care about segments where category is not 'producing'
      if (segmentCategory === "producing") {
        return false;
      }

      const segmentStart = Number(segment.range.ge);
      const segmentEnd = Number(segment.range.lt);

      // * ignore backwards segments (?)
      if (segmentStart > segmentEnd) {
        return false;
      }

      // If non-producing segment overlaps with this tick interval, we should skip it
      const isOverlap =
        // speed start overlaps
        (segmentStart < speedStart && speedStart < segmentEnd) ||
        // speed end overlaps
        (segmentStart < speedEnd && speedEnd < segmentEnd) ||
        // speed interval fully contains segment
        (speedStart < segmentStart && segmentEnd < speedEnd);

      return isOverlap;
    });

    // Skip this segment if it touches a non-producing segment
    if (nonProducingSegment) {
      continue;
    }

    // Try to find matching data dump row for this time range
    let targetRate = defaultTargetRate;
    let skuScale = 1;

    if (Array.isArray(dataDumpRows)) {
      const matchingRow = dataDumpRows.find((row: any) => {
        if (!row) {
          return false;
        }
        const ge = Number(row.ge);
        const lt = Number(row.lt);
        const tickStartTime = prevTick.ts * 1000;

        // Consider a row matching if it contains our tick start
        return ge <= tickStartTime && tickStartTime < lt;
      });

      if (matchingRow && matchingRow.targetRate) {
        targetRate = Number(matchingRow.targetRate);
        skuScale = Number(matchingRow.skuScale || 1);
      }
    }

    // Calculate expected time for producing one unit based on target rate
    const targetRatePerMin = targetRate;
    const targetRatePerMs = targetRatePerMin / ONE_MINUTE_OF_MS;
    const targetTimeMs = (1 / targetRatePerMs) * skuScale;

    // Calculate speed percentage: (target time / actual time) - 1
    // > 0 means faster than target (positive gain)
    // < 0 means slower than target (negative gain)
    const speedPercentageRaw = Math.round(
      (targetTimeMs / actualTimeMs - 1) * 100
    );

    // Adjust speed percentage to be relative to target speed
    // e.g. if target speed threshold is 90%, then 90% is +0%, 100% is +10%, 110% is +20% etc.
    const speedPercentage = speedPercentageRaw + 100 - speedGainLossTargetSpeed;

    // Categorize the segment
    let category: SpeedCategory;
    if (speedPercentage > 10) {
      category = SpeedCategory.ABOVE;
    } else if (speedPercentage < -10) {
      category = SpeedCategory.BELOW;
    } else {
      category = SpeedCategory.AT;
    }

    // Calculate expected output based on target rate
    const targetOutput = targetRatePerMs * actualTimeMs;
    const actualOutput = skuScale; // Each tick represents one output unit * skuScale

    segments.push({
      range: {
        ge: prevTick.ts * 1000,
        lt: currentTick.ts * 1000,
      },
      category,
      speedPercentage,
      targetOutput,
      actualOutput,
      targetRate: targetRatePerMin,
    });
  }

  return segments;
}
